import { z } from "zod";

/**
 * Cross-Validation Error Schema
 *
 * Contains blocking cross-form validations that span multiple forms.
 * These are critical business rules that prevent invalid data combinations.
 */
export const CrossValidationErrorSchema = z
  .object({
    // Auction end date for cross-validation with lease
    atAuction: z
      .object({
        auctionEndDate: z.date().optional(),
      })
      .optional()
      .nullable(),

    // Lease available date for cross-validation with auction
    forLease: z
      .object({
        availableFrom: z.date().optional(),
      })
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    // CROSS-FORM ERROR: Lease start must be after auction end
    const auctionEndDate = data.atAuction?.auctionEndDate;
    const leaseAvailableFrom = data.forLease?.availableFrom;

    if (auctionEndDate && leaseAvailableFrom) {
      if (leaseAvailableFrom <= auctionEndDate) {
        ctx.addIssue({
          code: "custom",
          message: "Available from date must be after the auction end date",
          path: ["forLease", "availableFrom"],
        });
        ctx.addIssue({
          code: "custom",
          message: "Auction must end before the lease availability date.",
          path: ["atAuction", "auctionEndDate"],
        });
      }
    }
  });

/**
 * Cross-Validation Warning Schema
 *
 * Contains TRUE cross-validation warnings that span multiple forms.
 * Single-field validations belong in their respective form-specific warning schemas.
 *
 * This schema validates relationships between auction and lease schedules.
 */
export const CrossValidationWarningSchema = z
  .object({
    // Auction end date for cross-validation with lease
    atAuction: z
      .object({
        auctionEndDate: z.date().optional(),
      })
      .optional()
      .nullable(),

    // Lease available date for cross-validation with auction
    forLease: z
      .object({
        availableFrom: z.date().optional(),
      })
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    // CROSS-FORM WARNING: Recommend at least 2 weeks between auction end and lease start
    // This gives landlords time to process applications, perform screenings, and prepare the property
    const auctionEndDate = data.atAuction?.auctionEndDate;
    const leaseAvailableFrom = data.forLease?.availableFrom;

    if (auctionEndDate && leaseAvailableFrom) {
      const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
      const timeDiff = leaseAvailableFrom.getTime() - auctionEndDate.getTime();

      if (timeDiff < TWO_WEEKS_MS) {
        const daysDiff = Math.ceil(timeDiff / (24 * 60 * 60 * 1000));
        ctx.addIssue({
          code: "custom",
          message: `Consider allowing at least 2 weeks between auction end (${auctionEndDate.toLocaleDateString()}) and move-in date (${leaseAvailableFrom.toLocaleDateString()}). Currently ${daysDiff} day${daysDiff !== 1 ? "s" : ""}. This buffer helps with tenant screening, lease signing, and property preparation.`,
          severity: "warning",
          path: ["forLease", "availableFrom"],
        });
      }
    }
  });

export type CrossValidationErrorData = z.infer<
  typeof CrossValidationErrorSchema
>;

export type CrossValidationWarningData = z.infer<
  typeof CrossValidationWarningSchema
>;
